import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import { GlobalOutlined } from "@/config/antd";
import {
  createTownHall,
  updateTownHall,
  fetchTownHallByAny,
  selectCurrentTownHall,
  selectTownHallLoading,
  selectTownHallSuccess,
} from "@/store/redux/slices/townhall/townhall_slice";
import { getOrganizationId } from "@/utils/token_functions";
import { useLoader } from "@/hooks/LoaderProvider";
import Back from "@/components/specific/Back";
import Input from "@/components/common/inputs/Input";
import TextEditor from "@/components/common/inputs/Text_Editor";
import FileInput from "@/components/common/inputs/File_Input";
import CustomScheduleInput from "@/components/specific/Custom_Schedule_Input";
import Button from "@/components/common/Button";
import AddPeopleCard from "@/pages/town_hall/components/Add_People_Card";
import Breadcrumb from "@/components/specific/Breadcrumb";
import Success from "@/components/common/alerts/Success";
import InviteMembersDialog from "@/ui/Invite_Members";
import Delete from "@/components/common/alerts/Delete";
import { fetchMultipleUsersAsync } from "@/store/redux/slices/user/user_slice";
import LocationInput from "@/components/common/inputs/Location_Input";
import Steps from "@/components/common/Steps";

const AddNewTownHall = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();

  const isEditMode = Boolean(id);
  const isLoading = useSelector(selectTownHallLoading);
  useSelector(selectCurrentTownHall);
  useSelector(selectTownHallSuccess);

  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [formData, setFormData] = useState({
    ...(isEditMode && { _id: "" }),
    title: "",
    description: "",
    schedule: {
      name: "",
      duration: "30min",
      speakers: [],
    },
    fileUrl: "",
    duration: "",
    customDate: "",
    customTimeFrom: "",
    customTimeTo: "",
    location: "",
    virtualMeetingUrl: "",
    speakers: [],
    attendees: [],
    isInviteMembersModalVisible: false,
    isDeleteModalVisible: false,
    memberToDelete: null,
    memberType: null, // 'speakers' or 'attendees'
  });

  // Define sections for tracking in the Steps component
  const formSections = [
    { title: "Townhall Information" },
    { title: "Schedules" },
    { title: "People" },
    { title: "Duration & Location" },
  ];

  // State to track the current active section
  const [currentSection, setCurrentSection] = useState(0);

  // Refs for each section to detect visibility
  const sectionRefs = useRef([]);

  // Track which sections have been completed
  const [sectionsCompleted, setSectionsCompleted] = useState([]);

  // Check if a section is complete
  const checkSectionCompletion = useCallback(() => {
    const completed = [];

    // Section 0: Townhall Information
    if (formData.title && formData.description) {
      completed.push(0);
    }

    // Section 1: Schedules
    if (formData.fileUrl) {
      completed.push(1);
    }

    // Section 2: People
    if (formData.speakers.length > 0 || formData.attendees.length > 0) {
      completed.push(2);
    }

    // Section 3: Duration & Location
    if (
      formData.duration &&
      (formData.duration !== "custom" ||
        (formData.customDate &&
          formData.customTimeFrom &&
          formData.customTimeTo)) &&
      (formData.location || formData.virtualMeetingUrl)
    ) {
      completed.push(3);
    }

    setSectionsCompleted(completed);
  }, [formData]);

  // Check section completion whenever formData changes
  useEffect(() => {
    checkSectionCompletion();
  }, [formData, checkSectionCompletion]);

  // Function to check which section is currently most visible
  useEffect(() => {
    const handleScroll = () => {
      const viewportHeight = window.innerHeight;
      const viewportCenter = window.scrollY + viewportHeight / 2;

      // Find the section closest to the center of the viewport
      let closestSection = 0;
      let closestDistance = Infinity;

      sectionRefs.current.forEach((sectionElement, index) => {
        if (sectionElement) {
          const rect = sectionElement.getBoundingClientRect();
          const sectionCenter = window.scrollY + rect.top + rect.height / 2;
          const distance = Math.abs(viewportCenter - sectionCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestSection = index;
          }
        }
      });

      setCurrentSection(closestSection);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Convert 12-hour format to 24-hour format
  const convertTo24Hour = (time12h) => {
    if (!time12h) return "";

    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":");

    if (hours === "12") {
      hours = "00";
    }

    if (modifier === "PM") {
      hours = parseInt(hours, 10) + 12;
    }

    return `${hours}:${minutes}`;
  };

  // Fetch townhall data when editing
  useEffect(() => {
    const fetchTownHallData = async () => {
      if (isEditMode) {
        try {
          showLoader();
          const result = await dispatch(
            fetchTownHallByAny({ key: "_id", value: id })
          ).unwrap();
          if (!result.error) {
            // The town hall data is inside the data array - access the first item
            const townHallData = result.data[0];

            let speakersData = [];
            if (townHallData.speakers?.length) {
              // Filter out null values and get valid IDs
              const validSpeakerIds = townHallData.speakers.filter((id) => id);
              if (validSpeakerIds.length) {
                const speakersResult = await dispatch(
                  fetchMultipleUsersAsync(validSpeakerIds)
                ).unwrap();
                speakersData = speakersResult.map((speaker) => ({
                  id: speaker._id,
                  name: speaker.fullname || speaker.username,
                  role: "speaker",
                  // Add other necessary fields
                }));
              }
            }

            // Fetch attendees data
            let attendeesData = [];
            if (townHallData.attendees?.length) {
              // Filter out null values and get valid IDs
              const validAttendeeIds = townHallData.attendees.filter(
                (id) => id
              );
              if (validAttendeeIds.length) {
                const attendeesResult = await dispatch(
                  fetchMultipleUsersAsync(validAttendeeIds)
                ).unwrap();
                attendeesData = attendeesResult.map((attendee) => ({
                  id: attendee._id,
                  name: attendee.fullname || attendee.username,
                  role: "attendee",
                  // Add other necessary fields
                }));
              }
            }

            // Fetch schedule speakers data if they exist
            let scheduleSpeakersData = [];
            if (townHallData.schedule?.speakers?.length) {
              // Filter out null values and get valid IDs
              const validScheduleSpeakerIds =
                townHallData.schedule.speakers.filter((id) => id);
              if (validScheduleSpeakerIds.length) {
                const speakersResult = await dispatch(
                  fetchMultipleUsersAsync(validScheduleSpeakerIds)
                ).unwrap();
                scheduleSpeakersData = speakersResult.map((speaker) => ({
                  id: speaker._id,
                  name: speaker.fullname || speaker.username,
                  role: "speaker",
                }));
              }
            }

            // Convert API time format (12-hour) to 24-hour format
            const startTime24 = convertTo24Hour(
              townHallData.duration.startTime
            );
            const endTime24 = convertTo24Hour(townHallData.duration.endTime);

            setFormData({
              _id: townHallData._id,
              title: townHallData.title,
              description: townHallData.description,
              schedule: {
                name: townHallData.schedule?.name || "",
                duration: townHallData.schedule?.duration || "30min",
                speakers: scheduleSpeakersData, // Use the fetched speakers data
              },
              fileUrl: townHallData.fileUrl || "",
              duration: "custom",
              customDate: `${townHallData.duration.startDate} - ${townHallData.duration.endDate}`,
              customTimeFrom: startTime24,
              customTimeTo: endTime24,
              location: townHallData.location || "",
              virtualMeetingUrl: townHallData.virtualMeetingUrl || "",
              speakers: speakersData, // Use the fetched speakers data
              attendees: attendeesData, // Use the fetched attendees data
              isInviteMembersModalVisible: false,
              isDeleteModalVisible: false,
              memberToDelete: null,
              memberType: null,
            });
          }
        } catch (error) {
          throw new Error("Failed to fetch townhall data:", error);
        } finally {
          hideLoader();
        }
      }
    };

    fetchTownHallData();
  }, [dispatch, hideLoader, id, isEditMode, showLoader]);

  const durationOptions = [
    { value: "24hours", label: "24 Hours" },
    { value: "48hours", label: "48 Hours" },
    { value: "custom", label: "Custom" },
  ];

  // Member handling functions
  const handleMembersSelected = (newMembers) => {
    const memberType = formData.memberType;

    if (memberType === "scheduleSpeakers") {
      // For schedule speakers, we need to update the schedule object
      setFormData((prev) => ({
        ...prev,
        schedule: {
          ...prev.schedule,
          speakers: [
            ...prev.schedule.speakers,
            ...newMembers.filter(
              (member) =>
                !prev.schedule.speakers.some(
                  (existing) => existing.id === member.id
                )
            ),
          ],
        },
        isInviteMembersModalVisible: false,
      }));
    } else {
      // For regular speakers and attendees (unchanged)
      setFormData((prev) => ({
        ...prev,
        [memberType]: [
          ...prev[memberType],
          ...newMembers.filter(
            (member) =>
              !prev[memberType].some((existing) => existing.id === member.id)
          ),
        ],
        isInviteMembersModalVisible: false,
      }));
    }
  };

  const handleDeleteMember = (member, type) => {
    if (type === "scheduleSpeakers") {
      setFormData((prev) => ({
        ...prev,
        memberToDelete: member.id,
        memberType: type,
        isDeleteModalVisible: true,
      }));
    } else {
      // Existing code
      setFormData((prev) => ({
        ...prev,
        memberToDelete: member.id,
        memberType: type,
        isDeleteModalVisible: true,
      }));
    }
  };

  const handleDeleteConfirm = () => {
    showLoader();
    const { memberToDelete, memberType } = formData;

    if (memberType === "scheduleSpeakers") {
      setFormData((prev) => ({
        ...prev,
        schedule: {
          ...prev.schedule,
          speakers: prev.schedule.speakers.filter(
            (m) => m.id !== memberToDelete
          ),
        },
        isDeleteModalVisible: false,
      }));
    } else {
      // Existing code
      setFormData((prev) => ({
        ...prev,
        [memberType]: prev[memberType].filter((m) => m.id !== memberToDelete),
        isDeleteModalVisible: false,
      }));
    }

    setTimeout(() => {
      hideLoader();
    }, 2000);
  };

  const handleEditorChange = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleScheduleChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      duration: value,
    }));
  };

  const handleCustomDateChange = (dateRange) => {
    setFormData((prev) => ({ ...prev, customDate: dateRange }));
  };

  const handleCustomTimeFromChange = (time24) => {
    const date = new Date(`2000-01-01T${time24}`);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    setFormData((prev) => ({
      ...prev,
      customTimeFrom: time24,
      apiTimeFrom: formattedTime,
    }));
  };

  const handleCustomTimeToChange = (time24) => {
    const date = new Date(`2000-01-01T${time24}`);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    setFormData((prev) => ({
      ...prev,
      customTimeTo: time24,
      apiTimeTo: formattedTime,
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddSpeakers = () => {
    setFormData((prev) => ({
      ...prev,
      memberType: "speakers",
      isInviteMembersModalVisible: true,
    }));
  };

  const handleAddAttendees = () => {
    setFormData((prev) => ({
      ...prev,
      memberType: "attendees",
      isInviteMembersModalVisible: true,
    }));
  };

  const handleSubmit = async () => {
    showLoader();
    try {
      let startDate, endDate, startTime, endTime;

      if (formData.duration === "custom") {
        // Handle custom date/time as before
        const [startDateStr, endDateStr] = formData.customDate.split(" - ");

        // Convert date strings to proper format (YYYY-MM-DD)
        const formatDate = (dateStr) => {
          const date = new Date(dateStr);
          return date.toISOString().split("T")[0];
        };

        startDate = formatDate(startDateStr);
        endDate = formatDate(endDateStr);
        startTime = formData.apiTimeFrom || formData.customTimeFrom;
        endTime = formData.apiTimeTo || formData.customTimeTo;
      } else {
        // For fixed durations (24hours, 48hours etc)
        const now = new Date();
        startDate = now.toISOString().split("T")[0];

        // Parse hours from duration value
        const hours = parseInt(formData.duration.replace("hours", ""));
        const endDateTime = new Date(now.getTime() + hours * 60 * 60 * 1000);
        endDate = endDateTime.toISOString().split("T")[0];

        // Set default times for fixed durations
        startTime = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        endTime = endDateTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }

      // let fileUrl = formData.fileUrl;
      // if (formData.fileUrl instanceof File) {
      //   try {
      //     const uploadResponse = await dispatch(
      //       uploadFile({ file: formData.fileUrl, folder: "townhalls" })
      //     ).unwrap();
      //     fileUrl = uploadResponse.fileUrl;
      //   } catch (uploadError) {
      //     console.error("File upload failed:", uploadError);
      //     hideLoader();
      //     // show an error modal here
      //     return;
      //   }
      // }

      // Sanitize the description
      const sanitizedDescription = DOMPurify.sanitize(formData.description, {
        ALLOWED_TAGS: [],
      });

      const townHallData = {
        _id: formData._id,
        title: formData.title.trim(),
        description: sanitizedDescription,
        // fileUrl: fileUrl,
        duration: {
          startDate,
          startTime,
          endDate,
          endTime,
        },
        location: formData.location,
        virtualMeetingUrl: formData.virtualMeetingUrl,
        schedule: {
          name: formData.schedule.name,
          duration: formData.schedule.duration,
          speakers: formData.schedule.speakers.map((speaker) => speaker.id), // Map to IDs
        },
        speakers: formData.speakers.map((speaker) => speaker.id),
        attendees: formData.attendees.map((attendee) => attendee.id),
        organizationID: getOrganizationId(),
      };

      if (isEditMode) {
        await dispatch(updateTownHall(townHallData)).unwrap();
        setIsSuccessVisible(true);
      } else {
        await dispatch(createTownHall(townHallData)).unwrap();
        setIsSuccessVisible(true);
      }
    } catch (error) {
      throw new Error(
        `Failed to ${isEditMode ? "update" : "create"} townhall:`,
        error
      );
    } finally {
      hideLoader();
    }
  };

  const handleSuccessProceed = () => {
    showLoader();
    setIsSuccessVisible(false);
    setTimeout(() => {
      hideLoader();
      if (isEditMode) {
        navigate(`/dashboard/townhall/${id}`);
      } else {
        navigate("/dashboard/townhall");
      }
    }, 2000);
  };

  const isFormValid =
    isEditMode ||
    (formData.title &&
      formData.description &&
      (formData.duration === "24hours" ||
        formData.duration === "48hours" ||
        (formData.duration === "custom" &&
          formData.customDate &&
          formData.customTimeFrom &&
          formData.customTimeTo)));

  if (isEditMode && isLoading) {
    return null;
  }

  // breadcrumb items
  const items = [
    { title: "Townhall", link: "/dashboard/townhall" },
    { title: "Add New", link: "" },
  ];

  return (
    <div className="w-full grid-cols-4 gap-10 py-6 text-left lg:grid sm:py-0">
      <div className="col-span-3 lg:border-r">
        <Breadcrumb items={items} />
        <div className="flex items-center justify-between mb-6">
          <Back />
        </div>

        <div className="my-5 rounded-lg lg:p-5 lg:border">
          <div className="w-full space-y-8">
            <h1 className="mb-2 text-left heading-medium sm:text-3xl">
              {isEditMode ? "Edit Townhall" : "Add New Townhall"}
            </h1>
            <p className="mb-6 text-left text-small">
              Let&apos;s get started with creating your townhall. Fill in the
              information below.
            </p>

            {/* Townhall Information Section */}
            <section
              ref={(element) => {
                sectionRefs.current[0] = element;
              }}>
              <h2 className="mb-4 text-bold">
                Describe what this townhall is all about
              </h2>
              <div className="space-y-4">
                <Input
                  label="TOWNHALL TITLE"
                  placeholder="Edit"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                />
                <TextEditor
                  label="TOWNHALL DESCRIPTION"
                  placeholder="Enter Texts"
                  value={formData.description}
                  onChange={handleEditorChange}
                />
              </div>
            </section>

            {/* Schedules Section */}
            <section
              ref={(element) => {
                sectionRefs.current[1] = element;
              }}>
              <h2 className="mb-4 text-bold">Schedules</h2>
              <p className="mb-4 font-normal text-lighterGrey">
                Add up images to make easily make your campaign easy to see
              </p>
              <FileInput
                id="schedule-upload"
                label="UPLOAD FILE"
                placeholder={
                  formData.fileUrl ? "File uploaded" : "File Name.png"
                }
                onChange={(file) => handleInputChange("fileUrl", file)}
              />
            </section>

            {/* Add People Section */}
            <section
              ref={(element) => {
                sectionRefs.current[2] = element;
              }}>
              <h2 className="mb-4 text-bold">Add People</h2>
              <p className="mb-4 font-normal text-lighterGrey">
                Select Event attendees and Speakers
              </p>
              <div className="space-y-4">
                <AddPeopleCard
                  title="ADD SPEAKERS"
                  onClick={handleAddSpeakers}
                  members={formData.speakers}
                  onRemoveMember={(member) =>
                    handleDeleteMember(member, "speakers")
                  }
                />
                <AddPeopleCard
                  title="ADD ATTENDEES"
                  onClick={handleAddAttendees}
                  members={formData.attendees}
                  onRemoveMember={(member) =>
                    handleDeleteMember(member, "attendees")
                  }
                />
              </div>
            </section>

            {/* Duration & Location Section */}
            <section
              ref={(element) => {
                sectionRefs.current[3] = element;
              }}>
              <h2 className="mb-4 text-bold">Duration & Location</h2>
              <p className="mb-4 font-normal text-lighterGrey">
                Set when and where the townhall will take place
              </p>
              <div className="space-y-4">
                <CustomScheduleInput
                  options={durationOptions}
                  value={formData.duration}
                  onChange={handleScheduleChange}
                  onCustomDateChange={handleCustomDateChange}
                  onCustomTimeFromChange={handleCustomTimeFromChange}
                  onCustomTimeToChange={handleCustomTimeToChange}
                  customDate={formData.customDate}
                  customTimeFrom={formData.customTimeFrom}
                  customTimeTo={formData.customTimeTo}
                />

                <LocationInput
                  label="LOCATE"
                  placeholder="Enter location"
                  value={formData.location}
                  onChange={(e) =>
                    handleInputChange("location", e.target.value)
                  }
                />
                <Input
                  label="VIRTUAL MEETING ADDRESS"
                  placeholder="https://meet.google.com/meeting-URL"
                  icon={<GlobalOutlined />}
                  value={formData.virtualMeetingUrl}
                  onChange={(e) =>
                    handleInputChange("virtualMeetingUrl", e.target.value)
                  }
                />

                <Button
                  text={isEditMode ? "Update Townhall" : "Create Townhall"}
                  onClick={handleSubmit}
                  disabled={!isFormValid}
                  color="primary"
                  className={`w-full mt-8 ${
                    !isFormValid &&
                    "bg-darkBlue text-primary cursor-not-allowed"
                  }`}
                  customBgColor={isFormValid ? "primary" : "darkBlue"}
                  customTextColor={isFormValid ? "white" : "primary"}
                />
              </div>
            </section>
          </div>

          {/* Success Dialog */}
          <Success
            isVisible={isSuccessVisible}
            header={
              isEditMode
                ? "Townhall Updated Successfully"
                : "Townhall Created Successfully"
            }
            paragraph={
              isEditMode
                ? "Your townhall has been successfully updated."
                : "Your townhall has been successfully created."
            }
            buttonText="Close"
            onButtonClick={handleSuccessProceed}
          />
        </div>
      </div>

      {/* Steps component for single-page form */}
      <div className="hidden mt-10 lg:block h-[90vh] sticky top-[80px]">
        <Steps
          current={currentSection}
          steps={formSections}
          mode="single-page"
          sectionsCompleted={sectionsCompleted}
        />
      </div>

      {/* Modals */}
      <InviteMembersDialog
        isVisible={formData.isInviteMembersModalVisible}
        onClose={() =>
          setFormData((prev) => ({
            ...prev,
            isInviteMembersModalVisible: false,
          }))
        }
        onMembersSelected={handleMembersSelected}
      />

      <Delete
        isVisible={formData.isDeleteModalVisible}
        onClose={() =>
          setFormData((prev) => ({ ...prev, isDeleteModalVisible: false }))
        }
        onProceed={handleDeleteConfirm}
        header={`Remove ${
          formData.memberToDelete
            ? formData[formData.memberType]?.find(
                (m) => m.id === formData.memberToDelete
              )?.name
            : ""
        }`}
        paragraph={`Are you sure you want to remove this member from the ${formData.memberType}?`}
        proceedButtonText="Remove"
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export default AddNewTownHall;
